import { useCallback, useEffect, useState } from "react";
import {
  Typography,
  Button,
  Row,
  Col,
  Flex,
  Layout,
  Space,
  Form,
  Input,
  List,
  Card,
  Alert,
  Empty,
  Spin,
} from "antd";
import { CaretUpOutlined } from "@ant-design/icons";
import { useThemedLogo } from "../../theme/useThemedLogo";
import { useRedmonkLogo } from "../../theme/useRedmonkLogo";
import PublicHeader from "../../components/PublicHeader";
import MarketingFooter from "../../components/MarketingFooter";
import Seo from "../../components/Seo";
import Reveal from "../../components/Reveal";
import {
  listFeatureRequests,
  submitFeatureRequest,
  voteFeatureRequest,
} from "../../utils/communityApi";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

function DuplicateNotice({ match, onUpvoteMatch, onSubmitAnyway, upvoting }) {
  return (
    <Alert
      type="info"
      showIcon
      message="Looks like this already exists"
      description={
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>
            “{match.title}” ({match.voteCount} vote
            {match.voteCount === 1 ? "" : "s"}) looks similar to what you're
            describing. Upvoting it helps us prioritize it faster than a new
            request would.
          </Text>
          <Space wrap>
            <Button
              type="primary"
              loading={upvoting}
              onClick={onUpvoteMatch}
            >
              Upvote "{match.title}"
            </Button>
            <Button type="link" onClick={onSubmitAnyway}>
              Submit as a new request anyway
            </Button>
          </Space>
        </Space>
      }
      style={{ marginBottom: "1.5rem" }}
    />
  );
}

const Community = () => {
  const logo = useThemedLogo();
  const redmonklogo = useRedmonkLogo();
  const accessToken = localStorage.getItem("accessToken");

  const [form] = Form.useForm();
  const [requests, setRequests] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [duplicateMatch, setDuplicateMatch] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  const [votingId, setVotingId] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const loadRequests = useCallback(() => {
    setListLoading(true);
    return listFeatureRequests()
      .then((res) => setRequests(res.data?.results || []))
      .catch(() => setError("Couldn't load feature requests. Please refresh."))
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleVote = async (id) => {
    setVotingId(id);
    try {
      const res = await voteFeatureRequest(id);
      const updated = res.data?.result;
      if (updated) {
        setRequests((prev) =>
          prev.map((item) => (item._id === id ? updated : item)),
        );
      }
    } catch (err) {
      if (err.response?.status !== 409) {
        setError("Couldn't record your vote. Please try again.");
      }
    } finally {
      setVotingId(null);
    }
  };

  const submit = async (values, force) => {
    setSubmitting(true);
    setError("");
    try {
      const res = await submitFeatureRequest({ ...values, force });
      if (res.data?.duplicate) {
        setDuplicateMatch(res.data.match);
        setPendingValues(values);
        return;
      }
      setDuplicateMatch(null);
      setPendingValues(null);
      setSubmitted(true);
      form.resetFields();
      await loadRequests();
    } catch (err) {
      setError(
        err.response?.data?.errors?.[0]?.message ||
          err.response?.data?.error ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onFinish = (values) => submit(values, false);

  const onUpvoteMatch = async () => {
    if (!duplicateMatch) return;
    await handleVote(duplicateMatch._id);
    setDuplicateMatch(null);
    setPendingValues(null);
    setSubmitted(true);
    form.resetFields();
  };

  const onSubmitAnyway = () => {
    if (pendingValues) submit(pendingValues, true);
  };

  return (
    <Layout className="marketing-page">
      <Seo
        title="Community Feature Requests"
        description="Tell us what Mailinflow should build next, and upvote the requests you care about most."
        path="/community"
      />
      <div
        className="container"
        style={{ position: "fixed", left: 0, right: 0, zIndex: 999 }}
      >
        <PublicHeader logo={logo} accessToken={accessToken} />
      </div>

      <main>
        <div className="container">
          <section style={{ padding: "9rem 0 2rem" }}>
            <Row justify="center">
              <Col xs={24} md={16} style={{ textAlign: "center" }}>
                <span className="mk-eyebrow">Community</span>
                <Title style={{ fontSize: "clamp(30px, 4vw, 44px)", margin: "1.25rem 0 1rem" }}>
                  Tell us what to build next
                </Title>
                <Paragraph style={{ fontSize: "1.05rem" }}>
                  Every feature we ship starts with a real request. Post
                  yours, or upvote one that's already here — the ones with
                  the most support get picked up first.
                </Paragraph>
              </Col>
            </Row>
          </section>

          <Reveal as="section" style={{ padding: "0 0 3rem" }}>
            <Row justify="center">
              <Col xs={24} sm={20} md={14}>
                <Card className="mk-card-hover">
                  {submitted && !duplicateMatch && (
                    <Alert
                      type="success"
                      showIcon
                      message="Thanks — we've got it."
                      description="We'll email you if we have questions or when it ships."
                      style={{ marginBottom: "1.5rem" }}
                      closable
                      onClose={() => setSubmitted(false)}
                    />
                  )}
                  {duplicateMatch && (
                    <DuplicateNotice
                      match={duplicateMatch}
                      upvoting={votingId === duplicateMatch._id}
                      onUpvoteMatch={onUpvoteMatch}
                      onSubmitAnyway={onSubmitAnyway}
                    />
                  )}
                  {error && (
                    <Alert
                      type="error"
                      showIcon
                      message={error}
                      style={{ marginBottom: "1.5rem" }}
                      closable
                      onClose={() => setError("")}
                    />
                  )}
                  <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="name"
                          label="Your name"
                          rules={[{ required: true, message: "Name is required" }]}
                        >
                          <Input placeholder="Jane Doe" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="email"
                          label="Email"
                          rules={[
                            { required: true, message: "Email is required" },
                            { type: "email", message: "Enter a valid email" },
                          ]}
                        >
                          <Input placeholder="jane@company.com" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      name="title"
                      label="What should we build?"
                      rules={[{ required: true, message: "Give it a short title" }]}
                    >
                      <Input placeholder="e.g. Add dark mode to the dashboard" />
                    </Form.Item>
                    <Form.Item
                      name="description"
                      label="What problem does this solve for you?"
                      rules={[{ required: true, message: "Description is required" }]}
                    >
                      <TextArea rows={4} placeholder="The more context, the better." />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Button type="primary" htmlType="submit" loading={submitting} block>
                        Submit request
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>
              </Col>
            </Row>
          </Reveal>

          <Reveal as="section" style={{ padding: "0 0 4rem" }}>
            <Row justify="center">
              <Col xs={24} sm={20} md={14}>
                <Title level={3} style={{ marginBottom: "1rem" }}>
                  Open requests
                </Title>
                {listLoading ? (
                  <Flex justify="center" style={{ padding: "2rem 0" }}>
                    <Spin />
                  </Flex>
                ) : requests.length === 0 ? (
                  <Empty description="No requests yet — be the first." />
                ) : (
                  <List
                    dataSource={requests}
                    renderItem={(item) => (
                      <List.Item key={item._id}>
                        <Card className="mk-card-hover" style={{ width: "100%" }}>
                          <Flex align="center" gap="middle">
                            <Button
                              shape="round"
                              icon={<CaretUpOutlined />}
                              loading={votingId === item._id}
                              onClick={() => handleVote(item._id)}
                            >
                              {item.voteCount}
                            </Button>
                            <div>
                              <Text strong>{item.title}</Text>
                              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                                {item.description}
                              </Paragraph>
                            </div>
                          </Flex>
                        </Card>
                      </List.Item>
                    )}
                  />
                )}
              </Col>
            </Row>
          </Reveal>
        </div>
      </main>

      <MarketingFooter logo={logo} redmonklogo={redmonklogo} />
    </Layout>
  );
};

export default Community;
